/**
 * ShopPanel -- Buy/sell items at the general store.
 *
 * HTML/CSS DOM overlay. Buy tab shows vendor stock, sell tab shows player inventory.
 * Prices: buy at full value, sell at 40%.
 *
 * @module ShopPanel
 */

import focusManager from '../../input/FocusManager.js';
import eventBus from '../../core/EventBus.js';
import townSystem from '../../systems/TownSystem.js';
import inventorySystem from '../../systems/InventorySystem.js';
import { RARITY_COLORS, RARITY_NAMES } from '../../systems/LootSystem.js';
import { SLOT_INFO } from '../../systems/InventorySystem.js';

export default class ShopPanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._tab = 'buy'; // 'buy' or 'sell'
    this._statusMsg = '';

    this._onInventoryChanged = () => {
      if (this._isOpen) this._render();
    };
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'shop-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Shop');
    this._el.setAttribute('data-scene', 'shop');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('shop', this._el);

    eventBus.on('inventory:changed', this._onInventoryChanged);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'shop') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    eventBus.off('inventory:changed', this._onInventoryChanged);
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';

    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = 'General Store';
    header.appendChild(title);

    // Gold
    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.id = 'shop-gold-display';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.setAttribute('aria-label', 'Close shop');
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);

    panel.appendChild(header);

    // Tabs
    const tabs = document.createElement('div');
    tabs.className = 'town-panel-tabs';

    const buyTab = document.createElement('button');
    buyTab.className = `town-panel-tab${this._tab === 'buy' ? ' town-panel-tab--active' : ''}`;
    buyTab.setAttribute('data-focusable', 'true');
    buyTab.textContent = 'Buy';
    buyTab.addEventListener('click', () => { this._tab = 'buy'; this._render(); focusManager.refreshGroup(); });
    tabs.appendChild(buyTab);

    const sellTab = document.createElement('button');
    sellTab.className = `town-panel-tab${this._tab === 'sell' ? ' town-panel-tab--active' : ''}`;
    sellTab.setAttribute('data-focusable', 'true');
    sellTab.textContent = 'Sell';
    sellTab.addEventListener('click', () => { this._tab = 'sell'; this._render(); focusManager.refreshGroup(); });
    tabs.appendChild(sellTab);

    panel.appendChild(tabs);

    // Status message
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    // Content
    const content = document.createElement('div');
    content.className = 'town-panel-content';

    if (this._tab === 'buy') {
      this._renderBuyTab(content);
    } else {
      this._renderSellTab(content);
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }

  _renderBuyTab(container) {
    const level = this._party?.partyLevel || 1;
    const stock = townSystem.getShopStock(this._townId, level);

    if (stock.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No items in stock.';
      container.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (const item of stock) {
      const price = item.buyPrice || townSystem.getItemBuyPrice(item);
      const canAfford = (this._party?.gold || 0) >= price;

      const card = this._createItemCard(item, `${price}g`, canAfford, () => {
        const result = townSystem.buyItem(this._party, this._townId, item.instanceId);
        this._statusMsg = result.message;
        this._render();
        focusManager.refreshGroup();
      });
      grid.appendChild(card);
    }

    container.appendChild(grid);
  }

  _renderSellTab(container) {
    const items = inventorySystem.getItems();

    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No items to sell.';
      container.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (const item of items) {
      const price = townSystem.getItemSellPrice(item);

      const card = this._createItemCard(item, `${price}g`, true, () => {
        const result = townSystem.sellItem(this._party, item.instanceId);
        this._statusMsg = result.message;
        this._render();
        focusManager.refreshGroup();
      });
      grid.appendChild(card);
    }

    container.appendChild(grid);
  }

  _createItemCard(item, priceText, enabled, onClick) {
    const card = document.createElement('div');
    card.className = `town-item-card${!enabled ? ' town-item-card--disabled' : ''}`;
    card.setAttribute('data-focusable', 'true');
    card.setAttribute('data-item-id', item.instanceId);

    const name = document.createElement('div');
    name.className = 'town-item-name';
    name.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
    name.textContent = item.name;
    card.appendChild(name);

    const meta = document.createElement('div');
    meta.className = 'town-item-meta';
    const slotInfo = SLOT_INFO[item.slot] || SLOT_INFO[item.slot?.replace(/\d+$/, '')] || { name: item.slot };
    meta.textContent = `${RARITY_NAMES[item.rarity] || 'Normal'} ${slotInfo.name}`;
    card.appendChild(meta);

    // Stats preview
    const stats = item.totalStats || item.baseStats || {};
    if (Object.keys(stats).length > 0) {
      const statsEl = document.createElement('div');
      statsEl.className = 'town-item-stats';
      const statParts = Object.entries(stats).slice(0, 3).map(([k, v]) => `+${v} ${k}`);
      statsEl.textContent = statParts.join(', ');
      card.appendChild(statsEl);
    }

    const price = document.createElement('div');
    price.className = 'town-item-price';
    price.textContent = priceText;
    card.appendChild(price);

    if (enabled) {
      card.addEventListener('click', onClick);
    }

    return card;
  }
}
